import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { SUB_LABEL, DEDUCTION_SUBS, WFH_RATE_PER_HOUR, WFH_RATE_LABEL, fmt } from "./constants";
import UndoToBusinessButton from "./UndoToBusinessButton";
import BiSelect from "@/components/BiSelect";
import BiDatePicker from "@/components/BiDatePicker";
import { formatDateShort } from "@/shared/format";

/**
 * Deductions tab — WFH hours (auto-calc at the ruleset's WFH fixed rate,
 * currently 70c/hr from 2026.1), tools <$300, self-education, uniform /
 * laundry, income protection, union fees, other. Rates flow from
 * `WFH_RATE_PER_HOUR` in `./constants` which reads the versioned KB.
 */
export default function DeductionsTab({ entries, onChange }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    sub_category: "wfh_hours",
    amount: "",
    hours: "",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const computeWfh = () => (Number(form.hours) || 0) * WFH_RATE_PER_HOUR;

  const save = async () => {
    let amt = Number(form.amount);
    if (form.sub_category === "wfh_hours") {
      amt = computeWfh();
    }
    if (!amt) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await api.post("/personal-tax/entries", {
        type: "deduction",
        sub_category: form.sub_category,
        amount: amt,
        hours: form.sub_category === "wfh_hours" ? Number(form.hours) || null : null,
        date: form.date,
        notes: form.notes,
        source: "manual",
      });
      toast.success("Deduction added");
      setShowAdd(false);
      setForm({ sub_category: "wfh_hours", amount: "", hours: "", date: new Date().toISOString().slice(0, 10), notes: "" });
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const removeEntry = async (eid) => {
    try {
      await api.delete(`/personal-tax/entries/${eid}`);
      await onChange();
    } catch { toast.error("Could not delete"); }
  };

  // Memoised sort so the deductions list doesn't re-order on every re-render
  // (parent re-renders often — filter toggles, dropdown state, etc.).
  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [entries]
  );

  return (
    <div className="space-y-3" data-testid="deductions-tab">
      {entries.length === 0 && (
        <div className="bi-surface p-4 text-center text-sm text-white/60">
          No personal deductions logged yet for this FY.
        </div>
      )}

      {sortedEntries.map((e) => (
        <div key={e.id} className="bi-surface p-3 flex items-center gap-2" data-testid={`ded-entry-${e.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">
              {SUB_LABEL[e.sub_category] || "Deduction"}
              {e.hours ? <span className="text-white/50 font-normal"> · {e.hours} hrs</span> : null}
            </div>
            <div className="text-[10px] text-white/40">{formatDateShort(e.date)}{e.notes ? ` · ${e.notes}` : ""}</div>
          </div>
          <div className="tabular-nums font-bold text-white">{fmt(e.amount)}</div>
          <UndoToBusinessButton entry={e} onDone={onChange} testIdSuffix={`ded-${e.id}`} />
          <button
            data-testid={`ded-delete-${e.id}`}
            onClick={() => removeEntry(e.id)}
            className="w-8 h-8 rounded-lg text-white/30 hover:text-red-400 inline-flex items-center justify-center"
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}

      {showAdd ? (
        <div className="bi-surface p-4 space-y-2" data-testid="add-deduction-form">
          <div className="font-heading font-bold text-sm">Add personal deduction</div>
          <BiSelect
            data-testid="ded-sub"
            className="w-full bi-input"
            value={form.sub_category}
            onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
          >
            {DEDUCTION_SUBS.map((s) => (
              <option key={s} value={s}>{SUB_LABEL[s]}</option>
            ))}
          </BiSelect>
          {form.sub_category === "wfh_hours" ? (
            <>
              <input
                data-testid="ded-hours"
                className="w-full bi-input"
                type="number" step="0.5"
                placeholder="Hours worked from home"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
              />
              <div className="text-[11px] text-white/50">
                ATO fixed rate {WFH_RATE_LABEL} → <b className="text-white">${computeWfh().toFixed(2)}</b>
              </div>
            </>
          ) : (
            <input
              data-testid="ded-amount"
              className="w-full bi-input"
              type="number" step="0.01"
              placeholder="Amount (AUD)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          )}
          <BiDatePicker
            data-testid="ded-date"
            className="w-full bi-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <input
            data-testid="ded-notes"
            className="w-full bi-input"
            placeholder="Notes (e.g. course name)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex gap-2">
            <button
              data-testid="ded-save"
              onClick={save}
              disabled={busy}
              className="flex-1 h-11 rounded-xl bi-accent-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="h-11 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider"
            >Cancel</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="add-deduction"
          onClick={() => setShowAdd(true)}
          className="w-full h-11 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add deduction
        </button>
      )}
    </div>
  );
}
